import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/firebase';
import { getAuth } from 'firebase/auth';
import { collection, getDocs, addDoc, updateDoc, query, orderBy, limit, deleteDoc, doc, onSnapshot, writeBatch, arrayRemove } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import GanttChart from '../components/GanttChart';
import { useBranding } from '../contexts/BrandingContext';
import { getCompanyLogoUrl, BRANDING_ELIGIBLE_PLAN_IDS } from '../utils/branding';
import { buildGanttExportPdf } from '../utils/ganttExportPdf';
import { buildGanttExportXml } from '../utils/ganttExportXml';
import { Document, Page, pdfjs } from 'react-pdf';
import { AlertTriangle, ArrowLeft, Eye, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import withProjectPermissions from '../components/withProjectPermissions';
import DrawingViewer from '../components/DrawingViewer';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/** One-line hint above the programme file picker (upload modal only). */
const PROGRAMME_FILE_TYPE_HINT = 'PDF only';

const NON_PDF_PROGRAMME_ALERT =
  'Only PDF files are accepted. Please use a PDF programme document.';

// ─── History helpers ──────────────────────────────────────────────────────────
function parseIso(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function diffDaysH(a, b) { return Math.round((b - a) / 86400000); }

function fmtAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}


function summariseChange(entry) {
  if (entry.action === 'created')  return `Added "${entry.taskName}"`;
  if (entry.action === 'deleted')  return `Deleted "${entry.taskName}"`;
  if (entry.action === 'reverted') return `Reverted to checkpoint "${entry.snapshotLabel}"`;

  const b = entry.before || {};
  const a = entry.after  || {};
  const parts = [];

  if (b.name !== a.name) parts.push(`renamed to "${a.name}"`);

  const bS = parseIso(b.start), aS = parseIso(a.start);
  const bE = parseIso(b.end),   aE = parseIso(a.end);
  if (b.start !== a.start || b.end !== a.end) {
    if (bS && aS && bE && aE) {
      const bDur = diffDaysH(bS, bE), aDur = diffDaysH(aS, aE);
      if (bDur === aDur && b.start !== a.start) {
        const shift = diffDaysH(bS, aS);
        parts.push(`shifted ${shift > 0 ? '+' : ''}${shift}d`);
      } else if (b.start !== a.start && b.end !== a.end) {
        parts.push('dates changed');
      } else if (b.start !== a.start) {
        const delta = diffDaysH(bS, aS);
        parts.push(`start ${delta > 0 ? '+' : ''}${delta}d`);
      } else {
        const delta = diffDaysH(bE, aE);
        parts.push(`end ${delta > 0 ? '+' : ''}${delta}d`);
      }
    } else { parts.push('dates changed'); }
  }

  const statusLabel = { not_started: 'Not started', in_progress: 'In progress', complete: 'Complete' };
  if (b.status !== a.status) parts.push(`→ ${statusLabel[a.status] || a.status}`);
  if (b.status === a.status && (b.percentComplete ?? 0) !== (a.percentComplete ?? 0))
    parts.push(`${a.percentComplete ?? 0}% complete`);
  if (b.team  !== a.team)  parts.push(`team → ${a.team}`);
  if (b.phase !== a.phase) parts.push(`phase → ${a.phase || 'none'}`);

  return parts.length
    ? `"${entry.taskName}" — ${parts.join(' · ')}`
    : `Updated "${entry.taskName}"`;
}

// ─── Export options modal ─────────────────────────────────────────────────────
function ExportModal({ onExport, onClose, isPlanEligible, hasCustomPlanBarColor, brandColor, hasBaselines }) {
  const [format, setFormat] = useState('pdf');
  const [opts, setOpts] = useState({
    showArrows:       false,
    showCriticalPath: false,
    showBaseline:     true,
    showStatusDots:   false,
    showProgress:     false,
    stripeMode:       'none',
    milestoneColor:   'navy',
  });

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggle = (key) => setOpts(o => ({ ...o, [key]: !o[key] }));
  const set    = (key, val) => setOpts(o => ({ ...o, [key]: val }));

  const sectionLabel = {
    fontSize: 10, fontWeight: 800, color: 'var(--cc-text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block',
  };

  const CheckRow = ({ k, label, disabled }) => (
    <button
      onClick={() => !disabled && toggle(k)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 12px', borderRadius: 8, textAlign: 'left', width: '100%',
        border: 'none', background: opts[k] && !disabled ? 'var(--cc-active-bg)' : 'transparent',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.38 : 1,
        transition: 'background 0.12s',
      }}
    >
      <div style={{
        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
        border: opts[k] && !disabled
          ? '2px solid var(--branding-app-primary)'
          : '2px solid var(--cc-subtle-border)',
        background: opts[k] && !disabled ? 'var(--branding-app-primary)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {opts[k] && !disabled && (
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="var(--branding-app-primary-fg)" strokeWidth="3.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
        )}
      </div>
      <span style={{ fontSize: 14, color: 'var(--cc-text-primary)', fontWeight: 500 }}>{label}</span>
    </button>
  );

  const PillRow = ({ k, options: pills }) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {pills.map(({ value, label, color }) => {
        const active = opts[k] === value;
        return (
          <button
            key={value}
            onClick={() => set(k, value)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: active ? 700 : 500,
              border: active ? '2px solid var(--branding-app-primary)' : '2px solid var(--cc-subtle-border)',
              background: active ? 'var(--branding-app-primary)' : 'transparent',
              color: active ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-secondary)',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            {color && <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)' }} />}
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onPointerDown={onClose}
    >
      <div
        style={{ background: 'var(--cc-card-bg)', borderRadius: 18, width: 440, boxShadow: '0 32px 72px rgba(0,0,0,0.28)', overflow: 'hidden' }}
        onPointerDown={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '22px 24px 18px', borderBottom: '1px solid var(--cc-subtle-border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--cc-active-bg)', color: 'var(--cc-text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--cc-text-primary)' }}>Export Programme</p>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--cc-text-muted)' }}>Choose a format to download</p>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cc-text-muted)', padding: 4, lineHeight: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 22 }}>

          {/* Format selector */}
          <div>
            <span style={sectionLabel}>Format</span>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {[
                { value: 'pdf', label: 'PDF', sub: null, icon: (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                )},
                { value: 'xml', label: 'XML', sub: null, icon: (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
                  </svg>
                )},
              ].map(f => {
                const active = format === f.value;
                return (
                  <button
                    key={f.value}
                    onClick={() => setFormat(f.value)}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center',
                      padding: '11px 14px', borderRadius: 10, textAlign: 'left', cursor: 'pointer',
                      border: active ? '2px solid var(--branding-app-primary)' : '2px solid var(--cc-subtle-border)',
                      background: active ? 'var(--cc-active-bg)' : 'transparent',
                      color: active ? 'var(--cc-text-primary)' : 'var(--cc-text-secondary)',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 700, fontSize: 13 }}>
                      {f.icon}
                      {f.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* PDF-specific options */}
          {format === 'pdf' && <>
            <div>
              <span style={sectionLabel}>Content</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 10 }}>
                <CheckRow k="showArrows"       label="Dependency arrows" />
                <CheckRow k="showCriticalPath" label="Critical path highlighting" />
                <CheckRow k="showBaseline"     label="Baseline bars" disabled={!hasBaselines} />
                <CheckRow k="showStatusDots"   label="Status indicators (at-risk / overdue)" />
                <CheckRow k="showProgress"     label="Progress fills" />
              </div>
            </div>

            {isPlanEligible && (
              <div>
                <div style={{ marginBottom: 14 }}>
                  <span style={sectionLabel}>Branding</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: 'var(--cc-text-secondary)' }}>Letterhead stripe</p>
                    <PillRow k="stripeMode" options={[
                      { value: 'none',  label: 'None' },
                      { value: 'navy',  label: 'Navy' },
                      ...(hasCustomPlanBarColor ? [{ value: 'brand', label: 'Brand colour', color: brandColor }] : []),
                    ]} />
                  </div>
                  <div>
                    <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: 'var(--cc-text-secondary)' }}>Milestone diamonds</p>
                    <PillRow k="milestoneColor" options={[
                      { value: 'navy',  label: 'Navy' },
                      ...(hasCustomPlanBarColor ? [{ value: 'brand', label: 'Brand colour', color: brandColor }] : []),
                    ]} />
                  </div>
                  {!hasCustomPlanBarColor && (
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--cc-text-muted)' }}>Set a brand colour in company settings to unlock brand colour options.</p>
                  )}
                </div>
              </div>
            )}
          </>}

        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--cc-subtle-border)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '10px 20px', borderRadius: 10, border: '1.5px solid var(--cc-subtle-border)', background: 'transparent', color: 'var(--cc-text-secondary)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={() => onExport({ ...opts, format })}
            style={{ padding: '10px 22px', borderRadius: 10, border: 'none', background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export
          </button>
        </div>
      </div>
    </div>
  );
}

const GanttLanding = ({ permissions }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [teams, setTeams] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState({ open: false, file: null });
  const [hoveredFileIdx, setHoveredFileIdx] = useState(null);
  const [tilePageStates, setTilePageStates] = useState({});
  const [pdfPageCounts, setPdfPageCounts] = useState({});
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [includeAllTeams, setIncludeAllTeams] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [selectedFileForUpload, setSelectedFileForUpload] = useState(null);
  const [phases, setPhases] = useState([]);
  const [areas, setAreas] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [locked, setLocked] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(null);
  const [savingRevision, setSavingRevision] = useState(false);
  const [scheduleRevision, setScheduleRevision] = useState(null); // { letter, version } | null
  const [revisionAction, setRevisionAction] = useState(null); // 'version' | 'revision' | null
  const [revisionNote, setRevisionNote] = useState('');

  const { company, tokens, hasCustomPlanBarColor } = useBranding();
  const isPlanEligible = BRANDING_ELIGIBLE_PLAN_IDS.includes((company?.planId || '').trim().toLowerCase());
  const brandColor = hasCustomPlanBarColor ? (tokens?.appPrimary || null) : null;

  // Get current user from Firebase Auth
  const auth = getAuth();
  const user = auth.currentUser;

  // Fetch project info
  useEffect(() => {
    if (!projectId) return;
    getDocs(collection(db, 'projects')).then(snapshot => {
      const doc = snapshot.docs.find(d => d.id === projectId);
      if (doc) {
        setProject({ id: doc.id, ...doc.data() });
        setPhases(doc.data()?.ganttPhases || []);
        setAreas(doc.data()?.ganttAreas || []);
        const rl = doc.data()?.scheduleRevisionLetter;
        const rv = doc.data()?.scheduleRevisionVersion;
        if (rl && rv) setScheduleRevision({ letter: rl, version: rv });
      }
    });
  }, [projectId]);

  // Fetch teams
  useEffect(() => {
    if (!projectId) return;
    getDocs(collection(db, `projects/${projectId}/teams`)).then(snapshot => {
      setTeams(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
  }, [projectId]);

  // Fetch tasks — real-time
  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(collection(db, `projects/${projectId}/tasks`), (snap) => {
      setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [projectId]);

  // Fetch uploaded programme files
  useEffect(() => {
    if (!projectId) return;
    const fetchUploadedFiles = async () => {
      try {
        // Get programme files from Firestore
        const programmeSnapshot = await getDocs(collection(db, `projects/${projectId}/programme`));
        
        const filesData = programmeSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().originalName,
          url: doc.data().downloadURL,
          path: doc.data().storagePath,
          type: doc.data().fileType,
          teams: doc.data().teams || [],
          uploadedAt: doc.data().uploadedAt
        }));
        
        setUploadedFiles(filesData);
      } catch (error) {
        console.error('Error fetching uploaded files:', error);
      }
    };
    
    fetchUploadedFiles();
  }, [projectId]);

  const isReadOnly = project?.status === 'closed' || !permissions?.canEdit;

  const auditUser = () => ({
    userId:   user?.uid || '',
    userName: user?.displayName || user?.email || 'Unknown',
  });

  // Save task (add or update) + audit log
  const handleTaskSave = async (task) => {
    if (isReadOnly) return;
    const { id, ...data } = task;
    const before    = id ? (tasks.find(t => t.id === id) || null) : null;
    const action    = id ? 'updated' : 'created';
    let   savedId   = id;

    if (id) {
      await updateDoc(doc(db, `projects/${projectId}/tasks`, id), data);
    } else {
      const ref = await addDoc(collection(db, `projects/${projectId}/tasks`), data);
      savedId = ref.id;
    }

    await addDoc(collection(db, `projects/${projectId}/ganttHistory`), {
      ...auditUser(),
      timestamp: new Date().toISOString(),
      action,
      taskId:   savedId,
      taskName: task.name || '',
      before,
      after: { id: savedId, ...data },
    });
  };

  // Delete task + clean up dependsOn references + audit log
  const handleTaskDelete = async (id) => {
    if (isReadOnly) return;
    const before = tasks.find(t => t.id === id) || null;

    await deleteDoc(doc(db, `projects/${projectId}/tasks`, id));
    const snap  = await getDocs(collection(db, `projects/${projectId}/tasks`));
    const batch = writeBatch(db);
    snap.docs.forEach(d => {
      if (d.data().dependsOn?.includes(id)) batch.update(d.ref, { dependsOn: arrayRemove(id) });
    });
    await batch.commit();

    await addDoc(collection(db, `projects/${projectId}/ganttHistory`), {
      ...auditUser(),
      timestamp: new Date().toISOString(),
      action:   'deleted',
      taskId:   id,
      taskName: before?.name || '',
      before,
      after: null,
    });
  };

  // Issue a revision snapshot (replaces checkpoint)
  const handleIssueRevision = async (action, note) => {
    if (isReadOnly) return;
    setSavingRevision(true);
    setRevisionAction(null);
    setRevisionNote('');
    try {
      let newLetter, newVersion;
      if (!scheduleRevision) {
        newLetter = 'A'; newVersion = 1;
      } else if (action === 'version') {
        newLetter = scheduleRevision.letter; newVersion = scheduleRevision.version + 1;
      } else {
        newLetter = String.fromCharCode(scheduleRevision.letter.charCodeAt(0) + 1); newVersion = 1;
      }
      const label = `Rev ${newLetter} (Version ${newVersion})`;
      const now   = new Date().toISOString();
      await addDoc(collection(db, `projects/${projectId}/ganttSnapshots`), {
        ...auditUser(),
        timestamp:       now,
        label,
        taskCount:       tasks.length,
        tasks:           tasks.map(t => ({ ...t })),
        isRevision:      true,
        revisionLetter:  newLetter,
        revisionVersion: newVersion,
        revisionNote:    note || '',
      });
      await updateDoc(doc(db, 'projects', projectId), {
        scheduleRevisionLetter:  newLetter,
        scheduleRevisionVersion: newVersion,
        scheduleRevisionDate:    now,
      });
      setScheduleRevision({ letter: newLetter, version: newVersion });
      if (showHistory) handleLoadHistory();
    } catch (e) {
      console.error('Failed to issue revision:', e);
    } finally {
      setSavingRevision(false);
    }
  };

  // Load audit log + snapshots and merge into unified timeline
  const handleLoadHistory = async () => {
    setLoadingHistory(true);
    try {
      const [histSnap, cpSnap] = await Promise.all([
        getDocs(query(collection(db, `projects/${projectId}/ganttHistory`),  orderBy('timestamp', 'desc'), limit(100))),
        getDocs(query(collection(db, `projects/${projectId}/ganttSnapshots`), orderBy('timestamp', 'desc'), limit(20))),
      ]);
      const items = [
        ...histSnap.docs.map(d => ({ type: 'change',     id: d.id, ...d.data() })),
        ...cpSnap.docs.map(d  => ({ type: d.data().isRevision ? 'revision' : 'checkpoint', id: d.id, ...d.data() })),
      ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setHistoryItems(items);
    } catch (e) {
      console.error('Failed to load history:', e);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Revert to a checkpoint — replaces all tasks with snapshot
  const handleRevert = async (snapshot) => {
    if (isReadOnly) return;
    const currentSnap  = await getDocs(collection(db, `projects/${projectId}/tasks`));
    const deleteBatch  = writeBatch(db);
    currentSnap.docs.forEach(d => deleteBatch.delete(d.ref));
    await deleteBatch.commit();

    const addBatch = writeBatch(db);
    snapshot.tasks.forEach(t => {
      const { id, ...data } = t;
      addBatch.set(doc(db, `projects/${projectId}/tasks`, id), data);
    });
    await addBatch.commit();

    await addDoc(collection(db, `projects/${projectId}/ganttHistory`), {
      ...auditUser(),
      timestamp:     new Date().toISOString(),
      action:        'reverted',
      taskId:        null,
      taskName:      null,
      before:        null,
      after:         null,
      snapshotLabel: snapshot.label,
    });

    setConfirmRevert(null);
    handleLoadHistory();
  };

  // Save phases order to project document
  const handlePhasesChange = async (newPhases) => {
    if (isReadOnly) return;
    setPhases(newPhases);
    try {
      await updateDoc(doc(db, 'projects', projectId), { ganttPhases: newPhases });
    } catch (e) {
      console.error('Failed to save phases:', e);
    }
  };

  // Save areas to project document
  const handleAreasChange = async (newAreas) => {
    if (isReadOnly) return;
    setAreas(newAreas);
    try {
      await updateDoc(doc(db, 'projects', projectId), { ganttAreas: newAreas });
    } catch (e) {
      console.error('Failed to save areas:', e);
    }
  };

  // Export schedule (PDF or XML) — called from modal with chosen options
  const revisionLabel = scheduleRevision
    ? `Rev ${scheduleRevision.letter} (Version ${scheduleRevision.version})`
    : null;

  const handleExport = async (opts) => {
    setShowExportModal(false);
    if (opts.format === 'xml') {
      try {
        buildGanttExportXml({ tasks, teams, phases, projectName: project?.name || '', revisionLabel });
      } catch (err) {
        console.error('XML export failed:', err);
      }
      return;
    }
    setExporting(true);
    try {
      const companyLogoUrl = getCompanyLogoUrl(company);
      await buildGanttExportPdf({
        tasks,
        teams,
        phases,
        areas,
        projectName:    project?.name    || '',
        projectAddress: project?.address || '',
        companyLogoUrl,
        brandColor,
        revisionLabel,
        options: opts,
      });
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  // Handle team selection
  const handleTeamSelection = (teamId) => {
    setSelectedTeams(prev => 
      prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  // Handle all teams toggle
  const handleAllTeamsToggle = () => {
    if (includeAllTeams) {
      setIncludeAllTeams(false);
      setSelectedTeams([]);
    } else {
      setIncludeAllTeams(true);
      setSelectedTeams(teams.map(team => team.id));
    }
  };

  // Handle file selection (preview mode) - PDF only for new uploads
  const handleFileSelect = (file) => {
    if (!file) return false;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'pdf') {
      alert(NON_PDF_PROGRAMME_ALERT);
      return false;
    }
    setSelectedFileForUpload(file);
    return true;
  };

  // Handle file upload (after confirmation)
  const handleFileUpload = async () => {
    if (isReadOnly) return;
    if (!selectedFileForUpload) return;
    const ext = selectedFileForUpload.name.split('.').pop()?.toLowerCase();
    if (ext !== 'pdf') {
      alert(NON_PDF_PROGRAMME_ALERT);
      return;
    }
    
    // Check if teams are selected
    if (!includeAllTeams && selectedTeams.length === 0) {
      alert('Please select at least one team or choose "All Teams" before uploading.');
      return;
    }
    
    setUploading(true);
    try {
      const storage = getStorage();
      const fileName = `${Date.now()}_${selectedFileForUpload.name}`;
      const fileRef = ref(storage, `projects/${projectId}/programme/${fileName}`);
      
      // Upload file to Firebase Storage
      await uploadBytes(fileRef, selectedFileForUpload);
      
      // Get download URL
      const downloadURL = await getDownloadURL(fileRef);
      
      // Create Firestore document for programme file
      const programmeDoc = {
        fileName: selectedFileForUpload.name,
        originalName: selectedFileForUpload.name,
        storagePath: fileRef.fullPath,
        downloadURL: downloadURL,
        fileType: selectedFileForUpload.name.split('.').pop().toLowerCase(),
        teams: includeAllTeams ? teams.map(team => ({ id: team.id, name: team.name, color: team.color })) : 
                                selectedTeams.map(teamId => {
                                  const team = teams.find(t => t.id === teamId);
                                  return { id: team.id, name: team.name, color: team.color };
                                }),
        uploadedAt: new Date().toISOString(),
        uploadedBy: user?.uid || 'unknown'
      };
      
      // Save to Firestore
      await addDoc(collection(db, `projects/${projectId}/programme`), programmeDoc);
      
      // Add to uploaded files list with team information
      const newFile = {
        name: selectedFileForUpload.name,
        url: downloadURL,
        path: fileRef.fullPath,
        type: selectedFileForUpload.name.split('.').pop().toLowerCase(),
        teams: programmeDoc.teams,
        uploadedAt: programmeDoc.uploadedAt,
        id: programmeDoc.id // This will be set after the document is created
      };
      
      setUploadedFiles(prev => [...prev, newFile]);
      
      console.log('File uploaded successfully:', selectedFileForUpload.name, 'for teams:', programmeDoc.teams);
      
      // Reset form and close modal
      setSelectedFileForUpload(null);
      setSelectedTeams([]);
      setIncludeAllTeams(false);
      setShowUploadModal(false);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handlePreviewFile = (file) => {
    if (file.type === 'pdf') {
      setViewer({ open: true, file });
    } else {
      window.open(file.url, '_blank');
    }
  };

  const handleTilePrevPage = (fileUrl, currentPage) => {
    if (currentPage > 1) setTilePageStates(prev => ({ ...prev, [fileUrl]: currentPage - 1 }));
  };

  const handleTileNextPage = (fileUrl, currentPage) => {
    const total = pdfPageCounts[fileUrl] || 1;
    if (currentPage < total) setTilePageStates(prev => ({ ...prev, [fileUrl]: currentPage + 1 }));
  };

  // Handle file deletion
  const handleDeleteFile = async (file) => {
    if (isReadOnly) return;
    try {
      const storage = getStorage();
      const fileRef = ref(storage, file.path);
      
      // Try to delete from Firebase Storage
      try {
        await deleteObject(fileRef);
        console.log('File deleted from storage successfully:', file.name);
      } catch (storageError) {
        // Handle case where file doesn't exist in storage
        if (storageError.code === 'storage/object-not-found') {
          console.log('File not found in storage, proceeding with database cleanup:', file.name);
        } else {
          // Re-throw other storage errors
          throw storageError;
        }
      }
      
      // Delete from Firestore if we have the document ID
      if (file.id) {
        try {
          await deleteDoc(doc(db, `projects/${projectId}/programme`, file.id));
          console.log('File deleted from Firestore successfully:', file.name);
        } catch (firestoreError) {
          console.warn('Failed to delete from Firestore, but continuing cleanup:', firestoreError);
        }
      }
      
      // Remove from uploaded files list regardless of storage/Firestore deletion status
      setUploadedFiles(prev => prev.filter(f => f.path !== file.path));
      
      console.log('File cleanup completed successfully:', file.name);
    } catch (error) {
      console.error('Error during file deletion process:', error);
      
      // Even if there's an error, try to remove from local state and database
      try {
        // Try to delete from Firestore as a fallback
        if (file.id) {
          await deleteDoc(doc(db, `projects/${projectId}/programme`, file.id));
          console.log('File deleted from Firestore as fallback:', file.name);
        }
        
        // Remove from uploaded files list
        setUploadedFiles(prev => prev.filter(f => f.path !== file.path));
        
        alert('File was removed from the system, but there may have been some cleanup issues.');
      } catch (fallbackError) {
        console.error('Fallback cleanup also failed:', fallbackError);
        alert('Failed to delete file. Please try again or contact support if the issue persists.');
      }
    }
  };



  if (!project) return <p style={{ padding: '40px' }}>Loading project...</p>;

  return (
    <>
      <style>{`
        .info-tooltip {
          position: relative;
          display: inline-block;
        }

        .info-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 16px;
          height: 16px;
          background: #e2e8f0;
          color: #64748b;
          border-radius: 50%;
          font-size: 10px;
          font-weight: 600;
          cursor: help;
          transition: all 0.2s ease;
        }

        .info-icon:hover {
          background: var(--branding-app-primary);
          color: var(--branding-app-primary-fg);
        }

        .tooltip-content {
          position: absolute;
          top: 125%;
          left: 50%;
          transform: translateX(-50%);
          background: var(--branding-app-primary);
          color: var(--branding-app-primary-fg);
          padding: 12px;
          border-radius: 6px;
          font-size: 11px;
          line-height: 1.4;
          white-space: nowrap;
          opacity: 0;
          visibility: hidden;
          transition: all 0.2s ease;
          z-index: 10000;
          min-width: 200px;
          white-space: normal;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .tooltip-content::after {
          content: '';
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-bottom-color: var(--branding-app-primary);
        }

        .info-tooltip:hover .tooltip-content {
          opacity: 1;
          visibility: visible;
        }
      `}</style>
    <div style={{ padding: '40px', flex: 1, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      {/* Back Button */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate(`/project/${projectId}`)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--cc-text-muted)',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = 'var(--branding-app-primary)';
          }}
          onMouseLeave={(e) => {
            e.target.style.color = '#6b7280';
          }}
        >
          <ArrowLeft size={20} />
          Back to project
        </button>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <div>
          <h1 style={{
            fontSize: 'clamp(20px, 2.2vw, 22px)',
            fontWeight: 600,
            color: 'var(--cc-text-primary)',
            marginBottom: 24
          }}>
            {project.name}
          </h1>
          {project.projectCode && (
            <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 12, lineHeight: 1.4 }}>
              <strong>Project Code:</strong> {project.projectCode}
            </p>
          )}
          <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 16, lineHeight: 1.4 }}>
            <strong>Address:</strong> {project.address}
          </p>
          <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 24, lineHeight: 1.4 }}>
            <strong>Postcode:</strong> {project.postcode}
          </p>
        </div>
      </div>

      {/* Programme Files */}
      <div style={{ background: 'var(--cc-card-bg)', borderRadius: 16, padding: '28px 32px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', marginBottom: 24 }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--cc-text-primary)', margin: 0 }}>Programme</h2>
          {uploadedFiles.length > 0 && (
            <span style={{
              background: 'var(--branding-app-surface-panel)',
              color: 'var(--cc-text-muted)',
              fontSize: 12, fontWeight: 600,
              padding: '2px 9px', borderRadius: 20,
              border: '1px solid var(--branding-app-surface-panel-border)',
            }}>
              {uploadedFiles.length}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {!isReadOnly && (
            <button
              onClick={() => setShowUploadModal(true)}
              style={{
                background: 'var(--branding-app-primary)',
                color: 'var(--branding-app-primary-fg)',
                border: 'none', borderRadius: 8,
                padding: '8px 18px', fontWeight: 500,
                cursor: 'pointer', fontSize: 14,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              + Upload
            </button>
          )}
        </div>

        {project?.status === 'closed' && (
          <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#92400e', fontWeight: 500 }}>
            This project is closed and read-only. Programme files can be viewed but not uploaded.
          </div>
        )}

        {uploadedFiles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--cc-text-muted)', fontSize: 14 }}>
            No programme files yet
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 24 }}>
            {uploadedFiles.map((file, index) => {
              const isHovered = hoveredFileIdx === index;
              const uploadDate = file.uploadedAt ? new Date(file.uploadedAt) : null;

              return (
                <div
                  key={file.id || index}
                  style={{
                    background: 'var(--cc-card-bg)',
                    borderRadius: 12,
                    overflow: 'hidden',
                    border: '1px solid var(--branding-app-surface-panel-border)',
                    boxShadow: isHovered ? '0 8px 28px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.04)',
                    transform: isHovered ? 'translateY(-2px)' : 'none',
                    transition: 'box-shadow 0.2s, transform 0.2s',
                    display: 'flex', flexDirection: 'column',
                  }}
                  onMouseEnter={() => setHoveredFileIdx(index)}
                  onMouseLeave={() => setHoveredFileIdx(null)}
                >
                  {/* Preview area */}
                  <div
                    style={{
                      position: 'relative', height: 220, overflow: 'hidden',
                      background: 'var(--branding-app-surface-panel)',
                      cursor: 'pointer', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    onClick={() => handlePreviewFile(file)}
                  >
                    <Document
                      file={file.url}
                      onLoadSuccess={({ numPages }) => {
                        if (!pdfPageCounts[file.url]) {
                          setPdfPageCounts(prev => ({ ...prev, [file.url]: numPages }));
                        }
                      }}
                    >
                      <Page
                        pageNumber={tilePageStates[file.url] || 1}
                        width={300}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                      />
                    </Document>

                    {/* PDF badge */}
                    <div style={{
                      position: 'absolute', top: 10, left: 10,
                      background: 'rgba(239,68,68,0.88)',
                      color: '#fff', fontSize: 10, fontWeight: 700,
                      padding: '3px 8px', borderRadius: 5, letterSpacing: '0.5px',
                      pointerEvents: 'none',
                    }}>
                      PDF
                    </div>

                    {/* Page navigation */}
                    {(pdfPageCounts[file.url] || 0) > 1 && (
                      <>
                        <button
                          style={{
                            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                            background: (tilePageStates[file.url] || 1) <= 1 ? 'rgba(156,163,175,0.5)' : 'rgba(0,0,0,0.65)',
                            color: '#fff', border: 'none', borderRadius: '50%',
                            width: 30, height: 30,
                            cursor: (tilePageStates[file.url] || 1) <= 1 ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          onClick={e => { e.stopPropagation(); handleTilePrevPage(file.url, tilePageStates[file.url] || 1); }}
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <button
                          style={{
                            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                            background: (tilePageStates[file.url] || 1) >= (pdfPageCounts[file.url] || 1) ? 'rgba(156,163,175,0.5)' : 'rgba(0,0,0,0.65)',
                            color: '#fff', border: 'none', borderRadius: '50%',
                            width: 30, height: 30,
                            cursor: (tilePageStates[file.url] || 1) >= (pdfPageCounts[file.url] || 1) ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                          onClick={e => { e.stopPropagation(); handleTileNextPage(file.url, tilePageStates[file.url] || 1); }}
                        >
                          <ChevronRight size={16} />
                        </button>
                        <div style={{
                          position: 'absolute', bottom: 8, right: 8,
                          background: 'rgba(0,0,0,0.55)', color: '#fff',
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                          pointerEvents: 'none',
                        }}>
                          {tilePageStates[file.url] || 1} / {pdfPageCounts[file.url]}
                        </div>
                      </>
                    )}

                    {/* Hover overlay */}
                    {isHovered && (
                      <div style={{
                        position: 'absolute', inset: 0,
                        background: 'rgba(0,0,0,0.28)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <div style={{
                          background: 'rgba(255,255,255,0.95)', color: '#1e293b',
                          borderRadius: 24, padding: '9px 26px',
                          fontWeight: 600, fontSize: 14,
                          display: 'flex', alignItems: 'center', gap: 8,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                          pointerEvents: 'none',
                        }}>
                          <Eye size={15} /> Open
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Card body */}
                  <div style={{ padding: '14px 16px 10px', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--cc-text-primary)', lineHeight: 1.4, wordBreak: 'break-word' }}>
                      {file.name}
                    </div>
                    {file.teams && file.teams.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 2 }}>
                        {file.teams.map((team, ti) => (
                          <span key={ti} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--cc-text-muted)' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: team.color, border: '1px solid var(--branding-app-surface-panel-border)', flexShrink: 0 }} />
                            {team.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {uploadDate && (
                      <div style={{ color: 'var(--cc-text-muted)', fontSize: 12, marginTop: 2 }}>
                        {uploadDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    )}
                  </div>

                  {/* Action bar */}
                  {!isReadOnly && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 12px 12px', gap: 6 }}>
                    <button
                      onClick={() => { setFileToDelete(file); setShowDeleteConfirm(true); }}
                      style={{
                        background: 'transparent', color: 'var(--cc-text-muted)',
                        border: '1px solid var(--branding-app-surface-panel-border)',
                        borderRadius: 6, padding: '5px 10px',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
                      }}
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Project Schedule */}
      <div
        style={{
          background: 'var(--cc-card-bg)',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          marginBottom: '24px',
        }}
      >
        <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--cc-text-primary)', margin: '0 0 24px 0' }}>
          Project Schedule
        </h2>
        <GanttChart
          tasks={tasks}
          teams={teams}
          projectId={projectId}
          phases={phases}
          onPhasesChange={handlePhasesChange}
          onTaskSave={handleTaskSave}
          onTaskDelete={handleTaskDelete}
          areas={areas}
          onAreasChange={handleAreasChange}
          onExport={() => setShowExportModal(true)}
          exporting={exporting}
          locked={isReadOnly ? true : locked}
          onToggleLock={isReadOnly ? undefined : () => setLocked(v => !v)}
          onShowHistory={() => { setShowHistory(true); handleLoadHistory(); }}
          revisionLabel={revisionLabel}
        />
      </div>

      {/* ── History panel ─────────────────────────────────────────────── */}
      {showHistory && (
        <>
          {/* Backdrop */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 2800 }}
            onClick={() => setShowHistory(false)}
          />
          {/* Drawer */}
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: 400,
            background: 'var(--cc-card-bg)',
            borderLeft: '1px solid var(--branding-app-surface-panel-border)',
            boxShadow: '-8px 0 32px rgba(0,0,0,0.14)',
            zIndex: 2900, display: 'flex', flexDirection: 'column',
          }}>
            {/* Header */}
            <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--branding-app-surface-panel-border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--cc-text-secondary)" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 9 15"/>
                </svg>
                <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: 'var(--cc-text-primary)' }}>History</span>
                <button onClick={() => setShowHistory(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cc-text-muted)', padding: 4, lineHeight: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => { setRevisionAction('version'); setRevisionNote(''); }}
                  disabled={isReadOnly || savingRevision || revisionAction !== null}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '7px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)',
                    fontSize: 12, fontWeight: 700, opacity: (savingRevision || revisionAction !== null) ? 0.6 : 1,
                  }}
                >
                  {savingRevision && revisionAction === null ? 'Saving…' : 'New Version'}
                </button>
                <button
                  onClick={() => { setRevisionAction('revision'); setRevisionNote(''); }}
                  disabled={isReadOnly || savingRevision || revisionAction !== null || !scheduleRevision}
                  title={!scheduleRevision ? 'Issue a version first before creating a new revision' : 'Increment revision letter (A→B)'}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '7px 10px', borderRadius: 8, cursor: (!scheduleRevision || savingRevision || revisionAction !== null) ? 'not-allowed' : 'pointer',
                    border: '1.5px solid var(--branding-app-surface-panel-border)',
                    background: 'transparent', color: 'var(--cc-text-secondary)',
                    fontSize: 12, fontWeight: 700,
                    opacity: (!scheduleRevision || savingRevision || revisionAction !== null) ? 0.4 : 1,
                  }}
                >
                  New Revision
                </button>
              </div>
              {/* Inline note prompt */}
              {revisionAction && (() => {
                const nextLetter = revisionAction === 'revision'
                  ? String.fromCharCode(scheduleRevision.letter.charCodeAt(0) + 1)
                  : (scheduleRevision?.letter || 'A');
                const nextVersion = revisionAction === 'version'
                  ? (scheduleRevision?.version || 0) + 1
                  : 1;
                const preview = `Rev ${nextLetter} (Version ${nextVersion})`;
                return (
                  <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--branding-app-surface-panel)', border: '1.5px solid var(--branding-app-surface-panel-border)' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--cc-text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Issuing <span style={{ color: 'var(--branding-app-primary)' }}>{preview}</span>
                    </div>
                    <input
                      value={revisionNote}
                      onChange={e => setRevisionNote(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleIssueRevision(revisionAction, revisionNote); if (e.key === 'Escape') { setRevisionAction(null); setRevisionNote(''); } }}
                      placeholder="Note"
                      autoFocus
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        padding: '8px 10px', borderRadius: 7, fontSize: 12, fontFamily: 'inherit',
                        border: '1.5px solid var(--branding-app-surface-panel-border)',
                        background: 'var(--cc-card-bg)', color: 'var(--cc-text-primary)',
                        outline: 'none', marginBottom: 8,
                      }}
                    />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => handleIssueRevision(revisionAction, revisionNote)}
                        style={{ flex: 1, padding: '6px 0', borderRadius: 7, border: 'none', cursor: 'pointer', background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)', fontSize: 12, fontWeight: 700 }}
                      >
                        Issue
                      </button>
                      <button
                        onClick={() => { setRevisionAction(null); setRevisionNote(''); }}
                        style={{ flex: 1, padding: '6px 0', borderRadius: 7, border: '1.5px solid var(--branding-app-surface-panel-border)', cursor: 'pointer', background: 'transparent', color: 'var(--cc-text-secondary)', fontSize: 12, fontWeight: 600 }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Timeline */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
              {loadingHistory && (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--cc-text-muted)', fontSize: 13 }}>Loading…</div>
              )}
              {!loadingHistory && historyItems.length === 0 && (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--cc-text-muted)', fontSize: 13 }}>
                  No history yet. Changes to the schedule will appear here.
                </div>
              )}
              {!loadingHistory && historyItems.map((item, i) => {
                if (item.type === 'revision' || item.type === 'checkpoint') {
                  const isConfirming = confirmRevert === item.id;
                  const isRev = item.type === 'revision';
                  const accentCol = isRev ? 'var(--branding-app-primary)' : 'var(--cc-text-muted)';
                  return (
                    <div key={item.id} style={{
                      margin: '8px 16px', borderRadius: 10,
                      background: isRev ? 'var(--cc-active-bg)' : 'var(--branding-app-surface-panel)',
                      border: isRev ? '1.5px solid var(--branding-app-primary)' : '1px solid var(--branding-app-surface-panel-border)',
                      padding: '12px 14px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        {isRev ? (
                          <span style={{ padding: '2px 8px', borderRadius: 5, background: 'var(--branding-app-primary)', color: 'var(--branding-app-primary-fg)', fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', flexShrink: 0 }}>
                            {item.label}
                          </span>
                        ) : (
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={accentCol} strokeWidth="2.5" strokeLinecap="round">
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                          </svg>
                        )}
                        {!isRev && <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: 'var(--cc-text-primary)' }}>{item.label}</span>}
                      </div>
                      {isRev && item.revisionNote && (
                        <div style={{ fontSize: 12, color: 'var(--cc-text-secondary)', marginBottom: 4, fontStyle: 'italic' }}>"{item.revisionNote}"</div>
                      )}
                      <div style={{ fontSize: 11, color: 'var(--cc-text-muted)', marginBottom: 10 }}>
                        {item.taskCount} task{item.taskCount !== 1 ? 's' : ''} · {item.userName} · {fmtAgo(item.timestamp)}
                      </div>
                      {isReadOnly ? null : !isConfirming ? (
                        <button
                          onClick={() => setConfirmRevert(item.id)}
                          style={{
                            padding: '5px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            border: '1.5px solid var(--branding-app-surface-panel-border)',
                            background: 'transparent', color: 'var(--cc-text-secondary)',
                            transition: 'all 0.12s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--branding-app-surface-panel-border)'; e.currentTarget.style.color = 'var(--cc-text-secondary)'; }}
                        >
                          Revert to this
                        </button>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>Replace current schedule?</span>
                          <button onClick={() => handleRevert(item)} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer' }}>Yes</button>
                          <button onClick={() => setConfirmRevert(null)} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-secondary)', border: 'none', cursor: 'pointer' }}>No</button>
                        </div>
                      )}
                    </div>
                  );
                }

                // Change entry
                const summary = summariseChange(item);
                const actionColor = item.action === 'deleted' ? '#ef4444' : item.action === 'created' ? '#10b981' : item.action === 'reverted' ? '#f59e0b' : 'var(--cc-text-muted)';
                const ActionIcon = () => {
                  if (item.action === 'created')  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={actionColor} strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
                  if (item.action === 'deleted')  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={actionColor} strokeWidth="2.5" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg>;
                  if (item.action === 'reverted') return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={actionColor} strokeWidth="2.5" strokeLinecap="round"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg>;
                  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={actionColor} strokeWidth="2.5" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z"/></svg>;
                };
                return (
                  <div key={item.id} style={{ padding: '9px 20px', display: 'flex', gap: 10, alignItems: 'flex-start', borderBottom: '1px solid var(--cc-subtle-border)' }}>
                    <div style={{ width: 22, height: 22, borderRadius: 6, background: `${actionColor}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                      <ActionIcon />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: 'var(--cc-text-primary)', lineHeight: 1.4, marginBottom: 3 }}>{summary}</div>
                      <div style={{ fontSize: 11, color: 'var(--cc-text-muted)' }}>{item.userName} · {fmtAgo(item.timestamp)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {showExportModal && (
        <ExportModal
          onExport={handleExport}
          onClose={() => setShowExportModal(false)}
          isPlanEligible={isPlanEligible}
          hasCustomPlanBarColor={hasCustomPlanBarColor}
          brandColor={brandColor}
          hasBaselines={tasks.some(t => t.baseline)}
        />
      )}

      {/* Programme Upload Modal */}
      {showUploadModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'var(--cc-card-bg)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '600px',
            width: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            position: 'relative'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '1px solid var(--branding-app-surface-panel-border)'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: 'var(--cc-text-primary)'
              }}>
                Upload PDF Programme
              </h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedTeams([]);
                  setIncludeAllTeams(false);
                  setSelectedFileForUpload(null);
                }}
                style={{
                  background: 'var(--branding-app-primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
                Close
              </button>
            </div>

            {/* Team Selection */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cc-text-primary)', marginBottom: '16px' }}>
                Select teams for programme access
              </h3>
              
              {/* All Teams Toggle */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--cc-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={includeAllTeams}
                    onChange={handleAllTeamsToggle}
                    style={{ width: '16px', height: '16px' }}
                  />
                  All Teams
                </label>
              </div>

                                {/* Individual Team Selection */}
                  {!includeAllTeams && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {teams.map(team => (
                        <button
                          key={team.id}
                          onClick={() => handleTeamSelection(team.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: selectedTeams.includes(team.id)
                              ? `2px solid ${team.color}`
                              : '2px solid transparent',
                            boxShadow: selectedTeams.includes(team.id) ? 'none' : 'inset 0 0 0 1px #e2e8f0',
                            background: 'var(--cc-card-bg)',
                            color: 'var(--cc-text-primary)',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <span style={{
                            display: 'inline-block',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: team.color,
                            border: '1px solid var(--branding-app-surface-panel-border)'
                          }} />
                          {team.name}
                        </button>
                      ))}
                    </div>
                  )}

              {/* Selection Summary */}
              <div style={{ 
                marginTop: '12px', 
                padding: '8px 12px', 
                background: 'var(--branding-app-surface-panel)', 
                borderRadius: '6px',
                fontSize: '13px',
                color: 'var(--cc-text-muted)'
              }}>
                {includeAllTeams 
                  ? `All ${teams.length} teams selected` 
                  : selectedTeams.length > 0 
                    ? `${selectedTeams.length} team${selectedTeams.length > 1 ? 's' : ''} selected` 
                    : 'No teams selected'
                }
              </div>
            </div>

            {/* File Upload Section */}
            {!selectedFileForUpload ? (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cc-text-primary)', marginBottom: '16px' }}>
                  Select file
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--cc-text-muted)', margin: '0 0 12px' }}>
                  {PROGRAMME_FILE_TYPE_HINT}
                </p>

                <input
                  id="pdf-upload-modal"
                  type="file"
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file && !handleFileSelect(file)) {
                      e.target.value = '';
                    }
                  }}
                />
                
                {/* Select File Button */}
                <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                  <button
                    onClick={() => document.getElementById('pdf-upload-modal').click()}
                    style={{
                      background: 'var(--branding-app-primary)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7,10 12,15 17,10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Select File
                  </button>
                </div>
                
                {/* Divider */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '16px',
                  color: 'var(--cc-text-muted)',
                  fontSize: '13px'
                }}>
                  <div style={{ flex: 1, height: '1px', background: 'var(--cc-active-bg)' }}></div>
                  <span style={{ padding: '0 12px' }}>or</span>
                  <div style={{ flex: 1, height: '1px', background: 'var(--cc-active-bg)' }}></div>
                </div>
                
                <div 
                  style={{ 
                    background: 'var(--branding-app-surface-panel)', 
                    borderRadius: '8px', 
                    padding: '20px',
                    border: '2px dashed #e2e8f0',
                    textAlign: 'center',
                    color: 'var(--cc-text-muted)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => document.getElementById('pdf-upload-modal').click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.background = '#eff6ff';
                    e.currentTarget.style.borderColor = 'var(--branding-app-primary)';
                    e.currentTarget.style.color = '#1d4ed8';
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.background = 'var(--branding-app-surface-panel)';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.color = '#64748b';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.background = 'var(--branding-app-surface-panel)';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.color = '#64748b';
                    
                    const files = Array.from(e.dataTransfer.files);
                    const acceptedFile = files.find(file => file.type === 'application/pdf');
                    if (acceptedFile) {
                      handleFileSelect(acceptedFile);
                    } else if (files.length > 0) {
                      alert(NON_PDF_PROGRAMME_ALERT);
                    }
                  }}
                >
                  <div style={{ fontSize: '16px' }}>
                    {uploading ? 'Uploading…' : 'Drag and drop here'}
                  </div>
                </div>
              </div>
            ) : (
              /* File Selected Display */
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--cc-text-primary)', marginBottom: '16px' }}>
                  Selected file
                </h3>
                
                <div style={{
                  background: 'var(--branding-app-surface-panel)',
                  borderRadius: '12px',
                  padding: '20px',
                  border: '1px solid var(--branding-app-surface-panel-border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  {/* File Thumbnail */}
                  <div style={{
                    width: '80px',
                    height: '100px',
                    background: 'var(--cc-card-bg)',
                    borderRadius: '8px',
                    border: '1px solid var(--branding-app-surface-panel-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                  }}>
                    {selectedFileForUpload.type === 'application/pdf' ? (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        background: 'var(--cc-card-bg)'
                      }}>
                        <div style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '6px',
                          overflow: 'hidden',
                          position: 'relative'
                        }}>
                          <iframe
                            src={`${URL.createObjectURL(selectedFileForUpload)}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none',
                              borderRadius: '6px',
                              transform: 'scale(1.02)',
                              transformOrigin: 'center',
                              pointerEvents: 'none',
                              userSelect: 'none'
                            }}
                            title="PDF Preview"
                            onLoad={(e) => {
                              // Make the iframe content unselectable
                              try {
                                const iframeDoc = e.target.contentDocument || e.target.contentWindow.document;
                                if (iframeDoc.body) {
                                  iframeDoc.body.style.userSelect = 'none';
                                  iframeDoc.body.style.webkitUserSelect = 'none';
                                  iframeDoc.body.style.mozUserSelect = 'none';
                                  iframeDoc.body.style.msUserSelect = 'none';
                                  iframeDoc.body.style.pointerEvents = 'none';
                                }
                              } catch (error) {
                                // Cross-origin restrictions may prevent this
                                console.log('Could not modify iframe content for security reasons');
                              }
                            }}
                          />
                        </div>
                        {/* PDF overlay with icon */}
                        <div style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          background: 'rgba(239, 68, 68, 0.9)',
                          color: '#fff',
                          fontSize: '10px',
                          fontWeight: '600',
                          padding: '2px 4px',
                          borderRadius: '4px',
                          lineHeight: 1,
                          zIndex: 1
                        }}>
                          PDF
                        </div>
                      </div>
                    ) : selectedFileForUpload.type.startsWith('image/') ? (
                      <img
                        src={URL.createObjectURL(selectedFileForUpload)}
                        alt="Preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '6px'
                        }}
                      />
                    ) : selectedFileForUpload.name.toLowerCase().endsWith('.doc') || selectedFileForUpload.name.toLowerCase().endsWith('.docx') ? (
                      <div style={{
                        fontSize: '28px',
                        color: 'var(--branding-app-primary)',
                        fontWeight: '600'
                      }}>
                        DOC
                      </div>
                    ) : (
                      <div style={{
                        fontSize: '28px',
                        color: 'var(--cc-text-muted)',
                        fontWeight: '600'
                      }}>
                        File
                      </div>
                    )}
                  </div>
                  
                  {/* File Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'var(--cc-text-primary)',
                      marginBottom: '4px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {selectedFileForUpload.name}
                    </div>
                    
                    <div style={{
                      fontSize: '14px',
                      color: 'var(--cc-text-muted)',
                      marginBottom: '8px'
                    }}>
                      {(selectedFileForUpload.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                    
                    <button
                      onClick={() => {
                        setSelectedFileForUpload(null);
                      }}
                      style={{
                        background: 'var(--cc-badge-bg)',
                        color: 'var(--cc-text-primary)',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#e5e7eb';
                        e.target.style.borderColor = '#9ca3af';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f3f4f6';
                        e.target.style.borderColor = '#d1d5db';
                      }}
                    >
                      Change File
                    </button>
                  </div>
                </div>
              </div>
            )}



            {/* Upload Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedTeams([]);
                  setIncludeAllTeams(false);
                  setSelectedFileForUpload(null);
                }}
                style={{
                  background: 'var(--cc-cancel-bg)',
                  color: 'var(--cc-text-primary)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleFileUpload}
                disabled={!selectedFileForUpload || uploading || (!includeAllTeams && selectedTeams.length === 0)}
                style={{
                  background: (!selectedFileForUpload || uploading || (!includeAllTeams && selectedTeams.length === 0)) ? '#cbd5e1' : 'var(--branding-app-primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '500',
                  cursor: (!selectedFileForUpload || uploading || (!includeAllTeams && selectedTeams.length === 0)) ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {uploading ? 'Uploading...' : !selectedFileForUpload ? 'Select File First' : (!includeAllTeams && selectedTeams.length === 0) ? 'Select Teams First' : 'Upload Programme'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DrawingViewer
        open={viewer.open}
        onClose={() => setViewer({ open: false, file: null })}
        file={viewer.file}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && fileToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'var(--cc-card-bg)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '90vw',
            textAlign: 'center'
          }}>
            {/* Warning Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#fef2f2',
              border: '2px solid #fecaca',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto'
            }}>
              <AlertTriangle size={32} color="#dc2626" />
            </div>

            {/* Confirmation Text */}
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '20px',
              fontWeight: '600',
              color: 'var(--cc-text-primary)'
            }}>
              Delete Programme File?
            </h3>
            
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '14px',
              color: 'var(--cc-text-muted)',
              lineHeight: '1.5'
            }}>
              Are you sure you want to delete <strong>"{fileToDelete.name}"</strong>? This action cannot be undone.
            </p>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setFileToDelete(null);
                }}
                style={{
                  background: 'var(--cc-cancel-bg)',
                  color: 'var(--cc-text-primary)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeleteFile(fileToDelete);
                  setShowDeleteConfirm(false);
                  setFileToDelete(null);
                }}
                style={{
                  background: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Delete File
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};


export default withProjectPermissions(GanttLanding, { requireEdit: true });